import { createSelector } from 'reselect';

import { getSystem } from './system.selectors';

const selectCrmState = createSelector(getSystem, ({ crm }) => crm);

const selectUsersState = state => state.users;

export const selectCrmContacts = createSelector(selectCrmState, ({ contacts }) => contacts);

export const selectCrmContactsTotalCount = createSelector(
  selectCrmState,
  ({ contactsTotalCount }) => contactsTotalCount,
);

export const selectCrmSelectedContact = createSelector(
  selectCrmState,
  ({ selectedContact }) => selectedContact,
);

export const selectCrmTags = createSelector(selectCrmState, ({ tags }) => tags);

export const selectCrmTagsOptions = createSelector(selectCrmTags, tags =>
  tags.map(({ id, name }) => ({ value: id, label: name })),
);

export const selectCrmContactNotes = createSelector(
  selectCrmState,
  ({ contactNotes }) => contactNotes,
);

const resolveUserName = (users, userId) => {
  const user = users?.[userId];

  if (!user) {
    return null;
  }

  const name = [user.firstName, user.middleInitial, user.lastName, user.suffix]
    .filter(Boolean)
    .join(' ');

  return name || null;
};

export const selectCrmContactNotesWithUsers = createSelector(
  [selectCrmContactNotes, selectUsersState],
  (notes, users) => {
    if (!Array.isArray(notes)) {
      return notes;
    }

    return notes.map(note => ({
      ...note,
      createdByName: note.createdByName || resolveUserName(users, note.createdByUserId),
      modifiedByName: note.modifiedByName || resolveUserName(users, note.modifiedByUserId),
    }));
  },
);

export const selectCrmContactActivityLog = createSelector(
  selectCrmState,
  ({ contactActivityLog }) => contactActivityLog,
);

export const selectCrmContactActivityLogWithUsers = createSelector(
  [selectCrmContactActivityLog, selectUsersState],
  (activities, users) => {
    if (!Array.isArray(activities)) {
      return activities;
    }

    return activities.map(activity => ({
      ...activity,
      performedByName:
        activity.performedByName || resolveUserName(users, activity.performedByUserId),
    }));
  },
);

export const selectCrmContactApplicationLinks = createSelector(
  selectCrmState,
  ({ contactApplicationLinks }) => contactApplicationLinks,
);

export const selectCrmContactEmails = createSelector(
  selectCrmState,
  ({ contactEmails }) => contactEmails,
);

export const selectCrmContactTasks = createSelector(
  selectCrmState,
  ({ contactTasks }) => contactTasks,
);

export const selectCrmContactChildren = createSelector(
  selectCrmState,
  ({ contactChildren }) => contactChildren,
);

export const selectCrmContactSiblings = createSelector(
  selectCrmState,
  ({ contactSiblings }) => contactSiblings,
);

export const selectCrmContactSubCompanyLinks = createSelector(
  selectCrmState,
  ({ contactSubCompanyLinks }) => contactSubCompanyLinks,
);

export const selectContactCustomFieldDefinitions = createSelector(
  selectCrmState,
  ({ contactPropertyDefinitions }) => contactPropertyDefinitions,
);

export const selectContactCustomFieldValues = createSelector(
  selectCrmState,
  ({ contactPropertyValues }) => contactPropertyValues,
);
